import {
  DMAutomation,
  DMAutomationContent,
  DMAutomationState,
  DMAutomationTrigger,
  DMAutomationTriggerContentType,
  KeywordRule,
  KeywordRuleMode,
} from "@markit/common.types";
import {
  collectionGroup,
  db,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
} from "../firebase";
import { isReplyDmTrigger, isReplyStoryTrigger } from "@markit/common.utils";
import { dmAutomationSubmissionConverter } from "./converters";
import {
  DmAutomationsCollection,
  getUserDmAutomationContentsRef,
  getUserDmAutomationData,
  getUserDmAutomationRef,
  getUserDmAutomationSubmissionsRef,
  getUserDmAutomationTriggersRef,
} from "./FirebaseUtils";

// Shape consumed directly by CreateDMAutomationWrapper's existingDmAutomation/
// existingDmTriggers/existingDmContents props.
export type DMAutomationExistingData = {
  automation: DMAutomation;
  triggers: DMAutomationTrigger[];
  contents: DMAutomationContent[];
};

// Duplicates a DM automation as a draft with a blank id, carrying over its most recent trigger and content (also with blank ids).
// Used for creating a new automation copy; nothing is saved until Publish. If campaign-based, the campaignId is also carried for editing context.
export const fetchDMAutomationDuplicate = async (
  userId: string,
  dmAutomationId: string
): Promise<DMAutomationExistingData> => {
  const dmAutomationData = await getUserDmAutomationData(
    userId,
    dmAutomationId
  );
  if (!dmAutomationData) {
    throw new Error("DM automation not found");
  }

  const triggersSnap = await getDocs(
    query(
      getUserDmAutomationTriggersRef(userId, dmAutomationId),
      orderBy("createdAt", "desc"),
      limit(1)
    )
  );
  const mostRecentTrigger = triggersSnap.docs[0]?.data();

  const contentsSnap = await getDocs(
    getUserDmAutomationContentsRef(userId, dmAutomationId)
  );

  const now = new Date().toISOString();
  const automation: DMAutomation = {
    ...dmAutomationData,
    id: "",
    title: `${dmAutomationData.title} (Copy)`,
    state: DMAutomationState.DRAFT,
    createdAt: now,
    updatedAt: now,
  };
  const triggers: DMAutomationTrigger[] = mostRecentTrigger
    ? [{ ...mostRecentTrigger, id: "", dmAutomationId: "", numTriggered: 0 }]
    : [];
  const contents: DMAutomationContent[] = contentsSnap.docs.map((doc) => ({
    ...doc.data(),
    id: "",
    dmAutomationId: "",
  }));

  return { automation, triggers, contents };
};

export const getNumDMAutomationTriggers = async (
  userId: string,
  dmAutomationId: string
) => {
  const dmAutomationTriggersRef = await getUserDmAutomationTriggersRef(
    userId,
    dmAutomationId
  );
  const query_ = query(dmAutomationTriggersRef);

  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

export const toggleDMAutomationState = async (
  userId: string,
  dmAutomationId: string,
  newState: DMAutomationState
) => {
  const dmAutomationRef = getUserDmAutomationRef(userId, dmAutomationId);
  await updateDoc(dmAutomationRef, { state: newState });
};

// Displays the content-type half of the trigger preview's title (e.g. "Specific Post")
export const getTriggerPostLabel = (trigger: DMAutomationTrigger) => {
  if (isReplyDmTrigger(trigger)) {
    return "Respond to DM";
  }
  if (isReplyStoryTrigger(trigger)) {
    switch (trigger.contentId) {
      case DMAutomationTriggerContentType.ALL_STORY:
        return "Any Story";
      case DMAutomationTriggerContentType.NEXT_STORY:
        return "Next Story";
      default:
        return "Specific Story";
    }
  }
  switch (trigger.contentId) {
    case DMAutomationTriggerContentType.ALL:
      return "All Posts";
    case DMAutomationTriggerContentType.NEXT:
      return "Next Post";
    default:
      return "Specific Post";
  }
};

// Gets the include terms for the given keyword rules
export const getDMAutomationKeywordIncludeTerms = (
  keywordRules: KeywordRule[]
) => {
  const includeKeywordRule = keywordRules.find(
    (keyword) => keyword.mode === KeywordRuleMode.INCLUDE
  );
  const terms = includeKeywordRule?.terms;
  if (terms) {
    return terms;
  }
  return [];
};

// Displays the label for the given keyword rules
export const displayKeywordRuleLabel = (keyboardRules: KeywordRule[]) => {
  const includeKeywordTerms = getDMAutomationKeywordIncludeTerms(keyboardRules);
  // Return undefined if a keyword rule exists but the terms are empty
  if (
    keyboardRules.length > 0 &&
    includeKeywordTerms.filter((term) => term.trim() !== "").length === 0
  ) {
    return undefined;
  }
  if (includeKeywordTerms.length > 0) {
    return "Keywords";
  }
  return "Any Comment";
};

// Joins quoted terms into a natural-language list: one term as-is, two joined by
// "or", three or more comma-separated with an Oxford comma before the final "or".
const joinTermsWithOr = (terms: string[]) => {
  const quoted = terms.map((term) => `"${term}"`);
  if (quoted.length === 1) {
    return quoted[0];
  }
  if (quoted.length === 2) {
    return quoted.join(" or ");
  }
  return `${quoted.slice(0, -1).join(", ")}, or ${quoted[quoted.length - 1]}`;
};

// Displays the actual keyword term(s) a trigger is scoped to (e.g. `Comment contains "ENTER"`),
// or "Any Comment" if it isn't scoped to any keywords.
export const getKeywordTriggerLabel = (
  keywordRules: KeywordRule[],
  trigger?: DMAutomationTrigger
) => {
  const isDm = !!trigger && isReplyDmTrigger(trigger);
  const isStory = !!trigger && isReplyStoryTrigger(trigger);
  const includeTerms = getDMAutomationKeywordIncludeTerms(keywordRules).filter(
    (term) => term.trim() !== ""
  );
  if (includeTerms.length === 0) {
    return isStory ? "Any Reply" : "Any Comment";
  }
  const noun = isDm ? "DM" : isStory ? "Reply" : "Comment";
  return `${noun} contains ${joinTermsWithOr(includeTerms)}`;
};

// Checks if the given DMAutomationTrigger is complete (add to Bit)
export const isDMAutomationTriggerComplete = (
  trigger: DMAutomationTrigger
): boolean => {
  if (!trigger.contentId?.trim()) return false;

  const keywordRules = trigger.keywordRules ?? [];
  if (keywordRules.length > 0) {
    const includeTerms = getDMAutomationKeywordIncludeTerms(keywordRules);
    if (includeTerms.every((term) => term.trim() === "")) return false;
  }

  const publicReplies = trigger.publicReplies ?? [];
  const populated = publicReplies.filter((r) => r.trim() !== "");
  if (publicReplies.length > 0 && populated.length < 3) return false;

  return true;
};

// Gets the number of submissions for a given trigger
export const getDMAutomationTriggerSubmissionCount = async (
  userId: string,
  dmAutomationId: string,
  triggerId: string
) => {
  const submissionsRef = getUserDmAutomationSubmissionsRef(
    userId,
    dmAutomationId
  );
  const query_ = query(submissionsRef, where("triggerId", "==", triggerId));
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

// Number of unique textable contacts across all submissions. Each Markit account
// maps to one phone number, so distinct submitter uids === distinct phone numbers.
export const getDMAutomationUniqueContactCount = async (
  userId: string,
  dmAutomationId: string
) => {
  const submissionsRef = getUserDmAutomationSubmissionsRef(
    userId,
    dmAutomationId
  );
  const query_ = query(submissionsRef, where("isFirstSubmission", "==", true));
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

export type RecentDmAutomationAnalytics = {
  automation: DMAutomation;
  completedAt: string;
};

// Fetches the automations this specific follower has completed for this creator,
// most recent first, across ALL of the creator's automations at once (via a
// collectionGroup query on dmAutomationSubmissions, scoped down to this creator
// via createdBy).
export const fetchUserDmAutomationSubmissions = async (
  creatorUid: string,
  followerUid: string,
  numToDisplay?: number
): Promise<RecentDmAutomationAnalytics[]> => {
  const submissionsQuery = query(
    collectionGroup(db, DmAutomationsCollection.DM_AUTOMATION_SUBMISSIONS),
    where("createdBy", "==", creatorUid),
    where("uid", "==", followerUid),
    orderBy("createdAt", "desc")
  ).withConverter(dmAutomationSubmissionConverter);
  let snapshot;
  try {
    snapshot = await getDocs(submissionsQuery);
  } catch (error) {
    console.error("Failed to fetch DM automation submissions:", error);
    return [];
  }
  if (snapshot.empty) {
    return [];
  }

  // A follower can submit to the same automation more than once (different
  // triggers) — only show its most recent completion.
  const seenAutomationIds = new Set<string>();
  const uniqueSubmissions = snapshot.docs
    .map((doc) => doc.data())
    .filter((submission) => {
      if (seenAutomationIds.has(submission.dmAutomationId)) {
        return false;
      }
      seenAutomationIds.add(submission.dmAutomationId);
      return true;
    });
  const limitedSubmissions = numToDisplay
    ? uniqueSubmissions.slice(0, numToDisplay)
    : uniqueSubmissions;

  const automations = await Promise.all(
    limitedSubmissions.map((submission) =>
      getUserDmAutomationData(creatorUid, submission.dmAutomationId)
    )
  );

  return limitedSubmissions
    .map((submission, index) => {
      const automation = automations[index];
      return automation
        ? { automation, completedAt: submission.createdAt }
        : undefined;
    })
    .filter((data): data is RecentDmAutomationAnalytics => data !== undefined);
};
